import { useState, useEffect, useRef, useCallback } from 'react'
import { authApi } from '../../api/client'
import { HiX, HiSearch, HiUserAdd, HiCheckCircle, HiBadgeCheck } from 'react-icons/hi'

export default function UserInviteModal({ onClose, onInvited }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [role, setRole] = useState('member')
  const [inviting, setInviting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const debounceRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const searchUsers = useCallback(async (q) => {
    if (q.length < 2) {
      setResults([])
      return
    }
    setSearching(true)
    try {
      const res = await authApi.searchEntra(q)
      setResults(res.data)
    } catch (err) {
      console.error('Entra search failed:', err)
      setResults([])
    } finally {
      setSearching(false)
    }
  }, [])

  const handleQueryChange = (e) => {
    const val = e.target.value
    setQuery(val)
    setSelectedUser(null)
    setError('')
    setSuccess('')

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => searchUsers(val), 300)
  }

  const handleSelect = (user) => {
    setSelectedUser(user)
    setQuery(user.display_name)
    setResults([])
    setError('')
    setSuccess('')
  }

  const handleInvite = async () => {
    if (!selectedUser) return
    if (selectedUser.already_in_org) {
      setError('This user is already in your organization.')
      return
    }

    setInviting(true)
    setError('')
    try {
      await authApi.inviteUser({
        entra_oid: selectedUser.entra_oid,
        email: selectedUser.email,
        display_name: selectedUser.display_name,
        job_title: selectedUser.job_title,
        department: selectedUser.department,
        role,
      })
      setSuccess(`${selectedUser.display_name} has been invited!`)
      setSelectedUser(null)
      setQuery('')
      onInvited?.()
    } catch (err) {
      if (err.response?.status === 409) {
        setError('This user already exists in the organization.')
      } else {
        setError('Failed to invite user. Please try again.')
      }
    } finally {
      setInviting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-surface-800 rounded-xl shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-2">
            <HiUserAdd className="w-5 h-5 text-brand-500" />
            <h2 className="text-lg font-semibold text-surface-800 dark:text-surface-200">Invite User</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700 rounded"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Success message */}
          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg text-sm">
              <HiCheckCircle className="w-5 h-5 shrink-0" />
              {success}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Search input */}
          <div className="relative">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
              Search Azure AD
            </label>
            <div className="relative">
              <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleQueryChange}
                placeholder="Search by name or email..."
                className="input pl-9"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Dropdown results */}
            {results.length > 0 && !selectedUser && (
              <div className="absolute z-10 mt-1 w-full bg-white dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {results.map((user) => (
                  <button
                    key={user.entra_oid}
                    onClick={() => handleSelect(user)}
                    disabled={user.already_in_org}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                      user.already_in_org
                        ? 'opacity-60 cursor-not-allowed bg-surface-50 dark:bg-surface-800'
                        : 'hover:bg-surface-50 dark:hover:bg-surface-600 cursor-pointer'
                    } border-b border-surface-100 dark:border-surface-600 last:border-b-0`}
                  >
                    <div className="w-8 h-8 bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 rounded-full flex items-center justify-center font-medium text-sm shrink-0">
                      {user.display_name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-surface-800 dark:text-surface-200 truncate text-sm">
                          {user.display_name}
                        </p>
                        {user.already_in_org && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium shrink-0">
                            <HiBadgeCheck className="w-3 h-3" />
                            In org
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-surface-500 dark:text-surface-400 truncate">{user.email}</p>
                      {user.job_title && (
                        <p className="text-xs text-surface-400 dark:text-surface-500 truncate">{user.job_title}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {query.length >= 2 && !searching && results.length === 0 && !selectedUser && (
              <div className="absolute z-10 mt-1 w-full bg-white dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-lg shadow-lg p-4 text-center text-sm text-surface-500 dark:text-surface-400">
                No users found matching "{query}"
              </div>
            )}
          </div>

          {/* Selected user card */}
          {selectedUser && !selectedUser.already_in_org && (
            <div className="p-4 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 rounded-full flex items-center justify-center font-semibold">
                  {selectedUser.display_name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-surface-800 dark:text-surface-200">{selectedUser.display_name}</p>
                  <p className="text-sm text-surface-500 dark:text-surface-400">{selectedUser.email}</p>
                  {selectedUser.job_title && (
                    <p className="text-xs text-surface-400 dark:text-surface-500 mt-0.5">{selectedUser.job_title}{selectedUser.department ? ` · ${selectedUser.department}` : ''}</p>
                  )}
                </div>
                <button
                  onClick={() => { setSelectedUser(null); setQuery(''); inputRef.current?.focus() }}
                  className="p-1 text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 rounded"
                >
                  <HiX className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Role selector */}
          {selectedUser && !selectedUser.already_in_org && (
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                Assign Role
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="input"
              >
                <option value="member">Member — Can create & manage own tasks</option>
                <option value="manager">Manager — Can manage all tasks & invite users</option>
                <option value="viewer">Viewer — Read-only access</option>
              </select>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              Close
            </button>
            {selectedUser && !selectedUser.already_in_org && (
              <button
                onClick={handleInvite}
                disabled={inviting}
                className="btn btn-primary"
              >
                {inviting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Inviting...
                  </>
                ) : (
                  <>
                    <HiUserAdd className="w-4 h-4 mr-2" />
                    Invite User
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
